// @ts-check
// Qué ejercicio está haciendo el alumno en cada editor.
//
// ## Por qué existe
//
// Los tres editores identifican el ejercicio con `?ejercicio=<slug>` en la
// URL. Ese parámetro se pierde al ir a otra pestaña y volver, así que cada
// página guardaba el último slug para recuperarlo.
//
// Las tres lo hacían con el MISMO bloque de código copiado -- un par
// `guardar`/`leer` sobre `localStorage`, con el mismo `try/catch` silencioso
// y el mismo recorte de espacios. Solo cambiaba la clave. Al hacer que cada
// ejercicio tenga su propio borrador (`state/ambito-borrador.js`) hizo falta
// además OLVIDAR el ejercicio, y una tercera copia de lo mismo en cada página
// era la ocasión de unificarlo.
//
// ## Las claves siguen siendo literales
//
// Igual que las de los borradores: `editor-drafts-namespaces.spec.js` exige
// encontrarlas escritas para comprobar que la limpieza de sesión las cubre, y
// cambiarlas un solo byte dejaría a los alumnos con el ejercicio olvidado.
// Por eso se enumeran aquí una a una y no se componen a partir del bloque.

/** @typedef {import('../utils/bloques.js').Bloque} Bloque */

/**
 * Clave de `localStorage` de cada editor. Valores EXACTOS a los que cada
 * página declaraba por su cuenta antes de unificarse.
 * @type {Record<Bloque, string>}
 */
export const CLAVE_EJERCICIO_SELECCIONADO = Object.freeze({
  diagrama: 'progedu.diagram-editor.ejercicio-seleccionado',
  pseudocodigo: 'progedu.pseudocode-editor.ejercicio-seleccionado',
  python: 'progedu.python-editor.ejercicio-seleccionado',
});

/**
 * @returns {Storage | null}
 */
function obtenerStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Recuerda el ejercicio que se está haciendo en `bloque`.
 *
 * Silencioso ante fallos de almacenamiento: perder este dato solo significa
 * que al volver a la pestaña habrá que elegir ejercicio otra vez, nunca que
 * el editor deje de funcionar.
 * @param {Bloque} bloque
 * @param {string} slug
 * @returns {void}
 */
export function recordarEjercicio(bloque, slug) {
  if (!slug) return;
  const clave = CLAVE_EJERCICIO_SELECCIONADO[bloque];
  if (!clave) return;
  try {
    obtenerStorage()?.setItem(clave, slug);
  } catch {
    // Ignorado a propósito.
  }
}

/**
 * Último ejercicio recordado en `bloque`, o `null`.
 * @param {Bloque} bloque
 * @returns {string | null}
 */
export function ejercicioRecordado(bloque) {
  const clave = CLAVE_EJERCICIO_SELECCIONADO[bloque];
  if (!clave) return null;
  try {
    const slug = obtenerStorage()?.getItem(clave) ?? null;
    return slug && slug.trim().length > 0 ? slug.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Olvida el ejercicio de `bloque`: el editor se abrirá sin ninguno
 * seleccionado.
 *
 * Lo usa la entrega de una traducción (`translation/entrega.js`): quien acaba
 * de convertir su programa a otra representación no está haciendo ningún
 * ejercicio, y el selector tiene que decirlo -- petición del usuario
 * (2026-08-06). Sin esto, el editor de destino se abría enseñando el último
 * ejercicio, que no tenía nada que ver con lo que había en pantalla.
 * @param {Bloque} bloque
 * @returns {void}
 */
export function olvidarEjercicio(bloque) {
  const clave = CLAVE_EJERCICIO_SELECCIONADO[bloque];
  if (!clave) return;
  try {
    obtenerStorage()?.removeItem(clave);
  } catch {
    // Ignorado a propósito.
  }
}
